import { cancelQueryReads } from 'src/modules/shared/utils/api/cancel-query-reads.util';
import type { QueryClient } from '@tanstack/react-query';
import {
  getTimelineSearchParams,
  valueMatches,
} from 'src/modules/shared/communications/utils/cache/query-matcher.util';
import type { CommunicationsRealtimeCallPayload } from '../../types/realtime.types';

/**
 * Cancels pending search reads that may predate an accepted Call snapshot.
 * Known identity mismatches preserve unrelated reads; unsupported filters stay
 * conservative because search owns membership. Cancellation never reverts data.
 * @param queryClient - Account-owned query cache.
 * @param payload - Accepted Call event with canonical resource identity.
 * @returns Whether existing coalesced HTTP recovery must restart affected reads.
 */
export const cancelPendingCallReads = (
  queryClient: QueryClient,
  payload: CommunicationsRealtimeCallPayload
): boolean => {
  const affected = queryClient.getQueryCache().findAll({
    fetchStatus: 'fetching',
    predicate: ({ queryKey }) => {
      const timeline = queryKey[0] === 'crm/timeline-items/search';
      if (!timeline && queryKey[0] !== 'crm/documents/funnel-conversations') {
        return false;
      }
      const filters = getTimelineSearchParams(queryKey)?.filters ?? [];
      if (filters.some((filter) => filter.or)) return true;
      const identity: Record<string, string | undefined> = {
        id: timeline
          ? payload.resourceId
          : String(payload.funnelConversationId),
        tenantId: payload.tenantId,
        funnelId: payload.funnelId?.toString(),
        funnelConversationId: String(payload.funnelConversationId),
        contactId: payload.resource.contactId.toString(),
      };
      return !filters.some(
        (filter) =>
          filter.field !== undefined &&
          identity[filter.field] !== undefined &&
          filter.value !== undefined &&
          (!filter.operator || ['eq', 'in'].includes(filter.operator)) &&
          !valueMatches(identity[filter.field], filter.value)
      );
    },
  });
  affected.forEach(({ queryKey }) => {
    cancelQueryReads(queryClient, { queryKey, exact: true });
  });
  return affected.length > 0;
};
